import { ADR, CEM4601 } from "../_constants/actionConstants";
import axios from "axios";
import _ from "lodash";
import { BASE_URL } from "../_constants/urlConstants";
import raiseError from "./raiseError";
import pdfActions from "./pdfActions";
import raiseWarning from "./raiseWarning";

const createPdfForSubmit = (adrId) => async (dispatch) => {
  dispatch({ type: ADR.GENERIC_REQUEST });
  const apiEndpoint = `${BASE_URL}/api/Cem4601Pdf/OnSubmit/${adrId}`;
  try {
    const pdfServiceOk = await dispatch(pdfActions.testService());
    if (pdfServiceOk) {
      const signUrlRes = await axios.get(apiEndpoint);
      if (signUrlRes.data.message) {
        raiseWarning(ADR.GENERIC_REQUEST_REJECTED, signUrlRes.data.message);
        return;
      }

      dispatch({ type: ADR.GENERIC_REQUEST_FULFILLED });
      if (!signUrlRes.data.signUrl) return;
      else {
        console.log("SignURL received:", signUrlRes.data.signUrl);
        // alert("SignURL received for submit, open new tab with iFrame for signature...");
      }
    }
  } catch (e) {
    // Any server-side errors should be logged at this point already.
    // Do not raise any error alerts to client.
    console.error("Failed to create CEM 4601 PDF on submit.");
    dispatch({ type: ADR.GENERIC_REQUEST_REJECTED });
    return;
  }
};

const signPdfForApproval = (adrId) => async (dispatch) => {
  dispatch({ type: ADR.GENERIC_REQUEST });
  const apiEndpoint = `${BASE_URL}/api/Cem4601Pdf/OnApproval/${adrId}`;
  try {
    const pdfServiceOk = await dispatch(pdfActions.testService());
    if (!pdfServiceOk) throw new Error();

    const signUrlRes = await axios.get(apiEndpoint);
    if (signUrlRes.data.message) {
      raiseWarning(ADR.GENERIC_REQUEST_REJECTED, signUrlRes.data.message);
      return;
    }
    dispatch({ type: ADR.GENERIC_REQUEST_FULFILLED });
    if (!signUrlRes.data.signUrl) return;
    else {
      console.log("SignURL received:", signUrlRes.data.signUrl);
      // alert("SignURL received for approval, open new tab with iFrame for signature...");
    }
  } catch (e) {
    // Reviewer needs to be notified that the PDF cannot be autofiled.
    alert(
      "This Daily Report was successfully accepted, but its PDF could not be autofiled " +
        "to FalconDMS due to an error from the ProDMS PDF server."
    );
    dispatch({ type: ADR.GENERIC_REQUEST_REJECTED });
  }
};

const requestPdf =
  (tabId, isPreviewOnly = true) =>
  async (dispatch, getState) => {
    dispatch({ type: CEM4601.REQUEST });

    const adr = getState().adr.openAdrs[tabId];
    if (!adr) {
      raiseError(CEM4601.REQUEST_REJECTED, null, "Could not find adr by tabId.");
      throw new Error("Could not find adr by tabId.");
    }

    const apiEndpoint = `${BASE_URL}/api/Cem4601Pdf/${adr.assistantDailyReportId}`;
    try {
      const fileRes = await axios.get(apiEndpoint, {
        responseType: "blob",
      });
      dispatch({ type: CEM4601.REQUEST_FULFILLED });

      // File is streamed back only for preview requests. Non-preview
      // requests only show PDF save success alert.
      if (isPreviewOnly && fileRes.data) {
        const fileName = fileRes.headers["content-disposition"]
          .split("filename=")[1]
          .split(";")[0]
          .trim();
        const pdfFileBlob = new Blob([fileRes.data], { type: "application/pdf" });

        const url = URL.createObjectURL(pdfFileBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      return;
    } catch (err) {
      // If error, do not show error msg, to allow re-attempt of client-side
      // PDF generation.
      dispatch({ type: CEM4601.REQUEST_REJECTED });
      console.error("PDF could not be processed by the server.", err);
      return Promise.reject(err);
    }
  };

/**
 * Request multiple PDFs for the provided Adrs.
 * This is a fire-and-forget operation, so no need to await. Toast messages for
 * status and the file download request are handled separately.
 * @param {number} projectId
 * @param {number[]} adrIds
 * @returns
 */
const requestPdfs = (adrIds) => async (dispatch) => {
  dispatch({ type: CEM4601.BATCH_REQUEST });

  const apiEndpoint = `${BASE_URL}/api/Cem4601Pdf/Batch/${adrIds}`;
  try {
    const pdfServiceOnline = await dispatch(pdfActions.testService());
    if (!pdfServiceOnline) throw new Error("PDF service is offline.");

    await axios.get(apiEndpoint);
    return;
  } catch (err) {
    dispatch({ type: CEM4601.REQUEST_REJECTED });
    console.error("PDF batch request failed.", err);
    return Promise.reject();
  }
};

const downloadZipFile = (filename) => async (dispatch) => {
  dispatch({ type: CEM4601.FILE_REQUEST });
  const apiEndpoint = `${BASE_URL}/api/Cem4601Pdf/ZipFile/${filename}`;
  console.log(apiEndpoint);
  try {
    const res = await axios.get(apiEndpoint, {
      responseType: "blob",
    });
    if (res.data) {
      const zipFileBlob = new Blob([res.data], { type: "application/zip" });

      const url = URL.createObjectURL(zipFileBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      dispatch({ type: CEM4601.FILE_REQUEST_FULFILLED });
    }
    return;
  } catch (err) {
    dispatch({ type: CEM4601.FILE_REQUEST_REJECTED });
    raiseError(CEM4601.FILE_REQUEST_REJECTED, err.response.data || err);
    return Promise.reject();
  }
};

const cem4601PdfActions = {
  createPdfForSubmit,
  downloadZipFile,
  requestPdf,
  requestPdfs,
  signPdfForApproval,
};

export default cem4601PdfActions;
